import type {Dispatch, SetStateAction} from 'react';
import {
  getCornerSize,
  getCornerX,
  getCornerY,
  makeWallStr,
  type KarelEnvironment,
} from '../util';
import type {IKarelState, KarelDirection} from '../karelInterfaces';
import {deepCopy} from '../../utils/general';

interface EditableWallsProps {
  state: KarelEnvironment;
  setWorldState: Dispatch<SetStateAction<IKarelState>>;
  isEditable: boolean;
}

export const EditableWalls = ({
  state,
  setWorldState,
  isEditable,
}: EditableWallsProps) => {
  const worldState = state.worldState;
  // eslint-disable-next-line no-var -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  var walls = [];
  for (let r = 0; r < worldState.nRows; r++) {
    for (let c = 0; c < worldState.nCols; c++) {
      const northExists = hasWall(worldState, r, c, 'North');
      if (r > 0) {
        walls.push(
          <EditableNorthWall
            key={`${r}-${c}-north`}
            r={r}
            c={c}
            state={state}
            exists={northExists}
            // eslint-disable-next-line @typescript-eslint/no-unused-expressions -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
            toggleWall={w => { isEditable ? toggleWall(w, setWorldState) : null; }}
          />,
        );
      }
      const eastExists = hasWall(worldState, r, c, 'East');
      if (c < worldState.nCols - 1) {
        walls.push(
          <EditableEastWall
            key={`${r}-${c}-east`}
            r={r}
            c={c}
            state={state}
            exists={eastExists}
            // eslint-disable-next-line @typescript-eslint/no-unused-expressions -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
            toggleWall={w => { isEditable ? toggleWall(w, setWorldState) : null; }}
          />,
        );
      }
    }
  }
  return <div>{walls}</div>;
};

function toggleWall(
  wall: {r: number; c: number; d: KarelDirection},
  setWorldState: Dispatch<SetStateAction<IKarelState>>,
): void {
  console.log('toggleWall called', wall);
  const wallString = makeWallStr(wall.r, wall.c, wall.d);
  setWorldState(oldWorldState => {
    const newWorldState = deepCopy(oldWorldState);
    // careful about getting the new beepers state
    if (!('walls' in newWorldState)) {
      newWorldState['walls'] = {};
    }
    const walls = newWorldState['walls'];
    const oldValue = wallString in walls ? walls[wallString] : false;
    const newValue = !oldValue;
    walls[wallString] = newValue;
    return newWorldState;
  });
}

interface EditableNorthWallProps {
  toggleWall: (wall: {r: number; c: number; d: KarelDirection}) => void;
  r: number;
  c: number;
  state: KarelEnvironment;
  exists: boolean;
}

const EditableNorthWall = ({
  toggleWall,
  r,
  c,
  state,
  exists,
}: EditableNorthWallProps) => {
  const height = getWallThickness(state);
  // eslint-disable-next-line prefer-const -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  let x = getCornerX(state, r, c) - 1;
  // eslint-disable-next-line prefer-const -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  let y = getCornerY(state, r, c) - 3;
  const selected = exists ? '-selected' : '';
  const onClick = (e: React.MouseEvent<HTMLDivElement>) => {
    toggleWall({r, c, d: 'North'});
    e.stopPropagation();
    e.preventDefault();
  };
  return (
    <div
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onClick(e as unknown as React.MouseEvent<HTMLDivElement>); }}
      role="button"
      tabIndex={0}
      aria-label={`Toggle north wall at row ${r}, column ${c}`}
      className={'karel-editable-wall' + selected}
      style={{
        marginLeft: x,
        marginTop: y,
        width: getCornerSize(state) - 2,
        height,
      }}
    />
  );
};

const getWallThickness = (state: KarelEnvironment) => {
  const cornerSize = getCornerSize(state);
  return cornerSize > 20 ? 4 : 2;
};

interface EditableEastWallProps {
  toggleWall: (wall: {r: number; c: number; d: KarelDirection}) => void;
  r: number;
  c: number;
  state: KarelEnvironment;
  exists: boolean;
}

const EditableEastWall = ({
  toggleWall,
  r,
  c,
  state,
  exists,
}: EditableEastWallProps) => {
  // eslint-disable-next-line prefer-const -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  let x = getCornerX(state, r, c + 1) - 3;
  // eslint-disable-next-line prefer-const -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  let y = getCornerY(state, r, c) - 1;
  const width = getWallThickness(state);
  const selected = exists ? '-selected' : '';

  const onClick = e => {
    console.log('wall click');
    toggleWall({r, c, d: 'East'});
    e.stopPropagation();
    e.preventDefault();
  };

  return (
    <div
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onClick(e); }}
      role="button"
      tabIndex={0}
      aria-label={`Toggle east wall at row ${r}, column ${c}`}
      className={'karel-editable-wall' + selected}
      style={{
        marginLeft: x,
        marginTop: y,
        width,
        height: getCornerSize(state) - 2,
      }}
    ></div>
  );
};

function hasWall(
  worldState: IKarelState,
  r: number,
  c: number,
  d: KarelDirection,
): boolean {
  if (!worldState.walls) {
    return false;
  }
  const wallStr = makeWallStr(r, c, d);
  if (!(wallStr in worldState.walls)) {
    return false;
  }
  return worldState.walls[wallStr];
}
